// =============================================================================
// ui/tabs/RiskOfRuinModal.tsx — the simulator behind the card's one-line figure.
// -----------------------------------------------------------------------------
// The card says "Risk of Ruin 12.4%". This says what that means: how often the
// account passes, how long passing usually takes, how bad the worst path got,
// and what changes if the horizon does.
//
// ── 🚨 IT IS THE CARD'S OWN MODEL, NOT A SECOND ONE ──
//
// The pool, the starting balance and the rules all arrive on the card model. It
// re-walks them with more paths and records more of the walk; it does not
// re-derive a single input. The old client does re-derive them, and the two
// disagree: its card resamples forward from the CURRENT balance while this modal
// starts from the account's ORIGINAL one, so the same account reads two
// different risks depending on which surface you looked at.
//
// ── 🚨 THE LOCK TOGGLE OPENS ON THE ACCOUNT'S OWN RULE (a fixed defect) ──
//
// The old client sets the checkbox from `lockBuffer > 0`, having thrown away the
// `lock` flag one line earlier. For `trail-then-freeze-at-start` and `static`
// the pad is zero but the floor DOES freeze, so those accounts open with their
// own rule switched off — and a floor that keeps trailing is tighter, so the
// simulator reports a WORSE account than the firm's rules describe.
//
// ── ⚠ SEEDED, AND "RE-RUN" RE-SEEDS ──
//
// D26 keeps `Math.random()` out of the model, so the same account and settings
// always draw the same fan — a risk number that moves on its own is not a risk
// number. But a Re-run button that changes nothing is a lie about what it does,
// so the run counter rides in the seed: opening is deterministic, pressing it
// genuinely resamples.
// =============================================================================

import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { money, moneyPlain, num } from '../format';
import {
  RUIN_DEFAULT_HORIZON, RUIN_HORIZONS, RUIN_THIN_POOL_DAYS,
  lockParams, seededRandom, simulateRuin,
  type AccountCardModel, type RuinSim,
} from '../../selectors/accountCards';

export interface RiskOfRuinModalProps {
  m: AccountCardModel;
  onClose: () => void;
}

export function RiskOfRuinModal({ m, onClose }: RiskOfRuinModalProps) {
  const auto = lockParams(m.ruinRules);
  const [horizon, setHorizon] = useState<number>(RUIN_DEFAULT_HORIZON);
  const [lock, setLock] = useState<boolean>(auto.lock);
  const [run, setRun] = useState(0);

  const sim = useMemo<RuinSim | null>(() => simulateRuin({
    days: m.ruinDays,
    start: m.ruinStart,
    rules: m.ruinRules,
    horizon,
    lock,
    rand: seededRandom(`${m.a.id}|${horizon}|${lock ? 'L' : 'T'}|${run}`),
  }), [m.ruinDays, m.ruinStart, m.ruinRules, m.a.id, horizon, lock, run]);

  const name = m.a.name || 'Untitled';

  return createPortal(
    <div
      className="modal-scrim"
      role="dialog"
      aria-modal="true"
      aria-label={`Risk of Ruin — ${name}`}
    >
      <div className="modal wide ror-modal">
        <header className="modal-head">
          <b>Risk of Ruin — {name}</b>
          <button type="button" className="btn" onClick={onClose}>×</button>
        </header>

        <div className="modal-body">
          {/* 🚨 A FINISHED ACCOUNT READS AS A FORECAST WITHOUT THIS. Everything
              below is phrased in the future tense; on a blown or passed account
              that is a what-if about something that has already happened. */}
          {m.terminal ? (
            <div className="ror-historical" role="note">
              <b>Historical — this account is {m.lifecycle}</b>
              <p>
                It is not trading any more. What follows is what would have
                happened over the next {num(horizon, 0)} days had it carried on
                from where it stopped. A review, not a forecast.
              </p>
            </div>
          ) : null}

          {sim ? (
            <>
              <Inputs m={m} sim={sim} />
              <Controls
                horizon={horizon}
                lock={lock}
                autoLock={auto.lock}
                onHorizon={setHorizon}
                onLock={setLock}
                onRerun={() => setRun((r) => r + 1)}
              />
              {sim.poolDays < RUIN_THIN_POOL_DAYS ? (
                <div className="ror-thin" role="note">
                  Only <b>{num(sim.poolDays, 0)}</b> real trading{' '}
                  {sim.poolDays === 1 ? 'day' : 'days'} to draw from — read these
                  odds as a direction, not a figure. They tighten as you log more.
                </div>
              ) : null}
              <Headline sim={sim} target={m.ruinRules.profitTarget} />
              <Verdict sim={sim} />
              <Fan sim={sim} />
              <Pool sim={sim} days={m.ruinDays} />
              <Model sim={sim} />
            </>
          ) : (
            // ⚠ The same two refusals the card makes, in the same words, rather
            // than a fabricated floor. Reaching here means the card said "—" and
            // the trader opened it to find out why.
            <p className="muted">{m.ruinBlocked ?? 'Nothing to resample yet.'}</p>
          )}
        </div>

        <footer className="modal-foot">
          <span className="grow" />
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

/** What the walk was given, before anything it produced. */
function Inputs({ m, sim }: { m: AccountCardModel; sim: RuinSim }) {
  return (
    <div className="ror-inputs">
      <span>Starts at <b>{moneyPlain(m.ruinStart, 0)}</b></span>
      <span>
        Drawdown <b>{moneyPlain(m.ruinRules.maxDrawdown, 0)}</b>
        {' '}(floor {moneyPlain(sim.startFloor, 0)})
      </span>
      <span>
        {sim.funded
          ? <>Mode <b>survival — no target</b></>
          : <>Target <b>{moneyPlain(m.ruinRules.profitTarget ?? 0, 0)}</b></>}
      </span>
      <span>Pool <b>{num(sim.poolDays, 0)} real days</b></span>
    </div>
  );
}

function Controls(
  { horizon, lock, autoLock, onHorizon, onLock, onRerun }: {
    horizon: number; lock: boolean; autoLock: boolean;
    onHorizon: (h: number) => void; onLock: (v: boolean) => void; onRerun: () => void;
  },
) {
  return (
    <div className="ror-controls">
      <label className="ror-ctl">
        <span>Horizon</span>
        <select value={horizon} onChange={(e) => onHorizon(Number(e.target.value))}>
          {RUIN_HORIZONS.map((h) => (
            <option key={h} value={h}>{h} days</option>
          ))}
        </select>
      </label>
      <label className="ror-ctl ror-lock">
        <input type="checkbox" checked={lock} onChange={(e) => onLock(e.target.checked)} />
        <span>Drawdown stops trailing once you are profitable</span>
      </label>
      {/* ⚠ SAYS WHICH WAY IT DIVERGES. Turning the lock off makes the floor chase
          the peak for ever, which is tighter than the account really is; turning
          it on when the rules do not gives it room it has not got. Either way
          the reader is now looking at a hypothetical, and only this line says so. */}
      {lock !== autoLock ? (
        <span className="ror-ctl-note">
          {autoLock
            ? 'This account’s rules DO freeze the floor — with it off you are looking at a harsher account than yours.'
            : 'This account’s rules DON’T freeze the floor — with it on you are looking at a safer account than yours.'}
        </span>
      ) : null}
      <span className="grow" />
      <button type="button" className="btn" onClick={onRerun}>Re-run</button>
    </div>
  );
}

/** Pass / blow / still-open, at the size the eye lands on first. */
function Headline({ sim, target }: { sim: RuinSim; target: number | null }) {
  const pct = (v: number) => `${num(v, 1)}%`;
  return (
    <div className={`ror-tiles${sim.funded ? ' two' : ''}`}>
      {sim.funded || target == null ? null : (
        <div className="ror-tile">
          <div className="ror-t-l">Pass</div>
          <div className="ror-t-v green">{pct(sim.passPct)}</div>
          <div className="ror-t-s">reach +{moneyPlain(target, 0)} first</div>
        </div>
      )}
      <div className="ror-tile">
        <div className="ror-t-l">Blow</div>
        <div className="ror-t-v red">{pct(sim.blowPct)}</div>
        <div className="ror-t-s">
          touch the {moneyPlain(sim.startFloor, 0)} floor
        </div>
      </div>
      <div className="ror-tile">
        <div className="ror-t-l">{sim.funded ? 'Survive' : 'Still going'}</div>
        <div className="ror-t-v">{pct(sim.openPct)}</div>
        <div className="ror-t-s">
          {sim.funded ? 'no breach over the horizon' : 'neither, at the horizon'}
        </div>
      </div>
    </div>
  );
}

/** The same numbers as a sentence, plus the four secondary figures. */
function Verdict({ sim }: { sim: RuinSim }) {
  const pct = (v: number) => `${num(v, 1)}%`;
  return (
    <>
      <div className="ror-verdict">
        {sim.funded ? (
          <>
            Over <b>{num(sim.horizon, 0)}</b> trading days you blow this account{' '}
            <b className="red">{pct(sim.blowPct)}</b> of the time and come through
            intact <b>{pct(sim.openPct)}</b>.
            {sim.blowPct > 25
              ? ' That is a lot — the drawdown is tight against the size of your daily swing.'
              : ''}
          </>
        ) : (
          <>
            Over <b>{num(sim.horizon, 0)}</b> trading days you pass this account{' '}
            <b className="green">{pct(sim.passPct)}</b> of the time and blow it{' '}
            <b className="red">{pct(sim.blowPct)}</b>.
            {sim.medianDaysToPass != null
              ? ` The runs that pass usually take about ${num(Math.round(sim.medianDaysToPass), 0)} days.`
              : ''}
            {sim.blowPct > sim.passPct
              ? ' Blowing it is currently likelier than passing — the drawdown is tight against the size of your daily swing.'
              : ''}
          </>
        )}
      </div>

      <div className={`ror-stats${sim.funded ? ' two' : ''}`}>
        {sim.funded ? null : (
          <>
            <Stat
              label="Typical days to pass"
              value={sim.medianDaysToPass != null ? num(Math.round(sim.medianDaysToPass), 0) : '—'}
              sub={sim.p25DaysToPass != null && sim.p75DaysToPass != null
                ? `middle half: ${num(Math.round(sim.p25DaysToPass), 0)}–${num(Math.round(sim.p75DaysToPass), 0)} days`
                : ''}
              tone="blue"
            />
            <Stat
              label="Fastest tenth by"
              value={sim.p10DaysToPass != null ? `${num(Math.round(sim.p10DaysToPass), 0)} d` : '—'}
              sub="the quickest 10% of passing runs"
            />
          </>
        )}
        <Stat
          label="Worst balance reached"
          value={moneyPlain(sim.worstBalance, 0)}
          sub={`across all ${num(sim.sims, 0)} paths`}
          tone="red"
        />
        {/* ⚠ `money`, NOT `moneyPlain`. This is a signed P&L, and the plain
            formatter puts the minus INSIDE the dollar sign — it rendered as
            "$-98" on Josh's real account. `moneyPlain` is for balances and
            costs, which are the other three figures in this row. */}
        <Stat
          label="Average day (your pool)"
          value={money(sim.avgPerDay, 0)}
          sub={`${num(sim.poolDays, 0)} days sampled`}
          tone={sim.avgPerDay >= 0 ? 'green' : 'red'}
        />
      </div>
    </>
  );
}

function Stat(
  { label, value, sub, tone }: {
    label: string; value: string; sub: string; tone?: 'green' | 'red' | 'blue';
  },
) {
  return (
    <div className="ror-stat">
      <div className="ror-s-l">{label}</div>
      <div className={`ror-s-v${tone ? ` ${tone}` : ''}`}>{value}</div>
      {sub ? <div className="ror-s-s">{sub}</div> : null}
    </div>
  );
}

const FAN_W = 1000;
const FAN_H = 300;
const PAD = { l: 74, r: 18, t: 14, b: 28 };

/**
 * The spread of simulated balances, day by day.
 *
 * ⚠ THE BAND IS 10th-to-90th, NOT the full range. One path in a thousand does
 * something absurd, and drawing the extremes would make every account look
 * identical — a huge grey cloud. Eight runs in ten land inside this shape.
 */
function Fan({ sim }: { sim: RuinSim }) {
  const { p10, p50, p90, horizon } = sim;
  const target = sim.targetBalance;
  const values = [...p10, ...p90, sim.startFloor, ...(target != null ? [target] : [])];
  let lo = Math.min(...values);
  let hi = Math.max(...values);
  const pad = (hi - lo) * 0.08 || 100;
  lo -= pad; hi += pad;

  const x = (d: number) => PAD.l + ((FAN_W - PAD.l - PAD.r) * d) / horizon;
  const y = (v: number) => PAD.t + (FAN_H - PAD.t - PAD.b) * (1 - (v - lo) / (hi - lo));

  const line = (series: readonly number[]) => series
    .map((v, d) => `${d ? 'L' : 'M'}${x(d).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');
  const band = `${line(p90)} ${p10
    .map((v, d) => ({ v, d }))
    .reverse()
    .map(({ v, d }) => `L${x(d).toFixed(1)} ${y(v).toFixed(1)}`)
    .join(' ')} Z`;

  const gridlines = [sim.startFloor, p50[0]!, ...(target != null ? [target] : [])];

  return (
    <div className="ror-fan">
      <div className="ror-sec">Simulated balance — the middle 80% of paths</div>
      <svg viewBox={`0 0 ${FAN_W} ${FAN_H}`} preserveAspectRatio="xMidYMid meet" role="img"
        aria-label={`Simulated balance over ${horizon} days`}>
        {gridlines.map((v) => (
          <g key={v}>
            <line x1={PAD.l} y1={y(v)} x2={FAN_W - PAD.r} y2={y(v)}
              stroke="var(--border-2)" strokeWidth={1} />
            <text x={PAD.l - 8} y={y(v) + 4} fill="var(--text-3)" fontSize={11} textAnchor="end">
              {moneyPlain(v, 0)}
            </text>
          </g>
        ))}
        <path d={band} fill="rgba(46,157,255,.16)" stroke="none" />
        {target != null ? (
          <line x1={PAD.l} y1={y(target)} x2={FAN_W - PAD.r} y2={y(target)}
            stroke="var(--green)" strokeWidth={1.5} strokeDasharray="5 4" />
        ) : null}
        <line x1={PAD.l} y1={y(sim.startFloor)} x2={FAN_W - PAD.r} y2={y(sim.startFloor)}
          stroke="var(--red)" strokeWidth={1.5} strokeDasharray="5 4" />
        <path d={line(p50)} fill="none" stroke="var(--text)" strokeWidth={2.5} />
        <text x={PAD.l} y={FAN_H - 7} fill="var(--text-3)" fontSize={11}>day 0</text>
        <text x={FAN_W - PAD.r} y={FAN_H - 7} fill="var(--text-3)" fontSize={11} textAnchor="end">
          day {horizon}
        </text>
      </svg>
      <div className="ror-legend">
        <span><i className="sw median" />Middle path</span>
        <span><i className="sw band" />8 runs in 10 land in here</span>
        {target != null ? <span><i className="sw target" />Profit target</span> : null}
        <span><i className="sw floor" />Drawdown floor</span>
      </div>
    </div>
  );
}

/**
 * The real days being resampled, as bars.
 *
 * 🚨 THIS IS THE HONEST PART OF THE WHOLE SCREEN. Every percentage above is a
 * rearrangement of exactly these days — if they are unrepresentative, so is all
 * of it, and showing them lets the trader see that for themselves rather than
 * take the model's word.
 */
function Pool({ sim, days }: { sim: RuinSim; days: readonly number[] }) {
  const max = Math.max(...days.map(Math.abs)) || 1;
  return (
    <div className="ror-pool">
      <div className="ror-sec">
        Days being resampled — your {num(sim.poolDays, 0)} real trading days
      </div>
      <div className="ror-pool-bars">
        {days.map((v, i) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            className={`ror-day${v >= 0 ? '' : ' down'}`}
            style={{ height: `${Math.max(2, Math.round((34 * Math.abs(v)) / max))}px` }}
            title={money(v)}
          />
        ))}
      </div>
    </div>
  );
}

/** What the model does and does not do, in the trader's words. */
function Model({ sim }: { sim: RuinSim }) {
  return (
    <p className="ror-model">
      <b>How this works:</b> each simulated day is one of your real trading days,
      picked at random and put back — {num(sim.sims, 0)} runs of{' '}
      {num(sim.horizon, 0)} days.{' '}
      {/* 🚨 MEASURED, NOT ESTIMATED — and the first version of this sentence said
          "a tenth of a point", which was wrong by twentyfold. Over 40 seeds and
          three books the gap averages 0.8 points and reaches 2.3 where the rate
          sits near the middle; the card runs 1,500 paths because it runs once
          per card on a screen that holds fourteen of them. A claim about
          precision has to come from measuring it. */}
      The figure on the card is this same model run at a fraction of the paths,
      so it can sit a point or two either side of this one — this is the careful
      reading of the two.{' '}
      {/* 🚨 THE LIMITATION THAT MATTERS MOST, AND IT IS NOT A FOOTNOTE. A firm
          kills the account the moment the floor is touched intraday. This model
          only looks at where each day CLOSES, so it understates ruin for anyone
          who runs deep heat and recovers — which is most people. */}
      The floor is only checked <b>at the end of each day</b>, so a day that dips
      through it and comes back does not count as blown here — your firm would
      count it. The lock is{' '}
      <b>{sim.lock ? 'on' : 'off'}</b>
      {sim.lock && sim.lockBuffer
        ? ` (the floor stops rising at ${moneyPlain(sim.lockBuffer, 0)} above your start)`
        : ''}
      . And it assumes you keep trading the way you have been — it cannot see a
      change in the market or in you.
    </p>
  );
}
